import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Sparkles, ArrowRight, X } from 'lucide-react';
import Header from '../components/Header';
import CategoryBar from '../components/CategoryBar';
import FeaturedMarket from '../components/FeaturedMarket';
import ValueProps from '../components/ValueProps';
import MarketGrid from '../components/MarketGrid';
import Footer from '../components/Footer';
import OrderbookModal from '../components/OrderbookModal';
import SEO from '../components/SEO';
import MarketJsonLd from '../components/MarketJsonLd';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { trackFunnelEvent } from '../services/funnelService';
import { ROUTES, BASE_URL } from '../seo/routeMetadata';

const HOMEPAGE_META = ROUTES['/'];

const CREATE_TOOLTIP_KEY = 'amp_create_market_tooltip_dismissed';

const HomePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [closingDateFilter, setClosingDateFilter] = useState('all');
  const [tradeOpen, setTradeOpen] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [showCreateTooltip, setShowCreateTooltip] = useState(false);
  const { isSubscribed } = useAuth();

  // Show the onboarding tooltip to first-time visitors, once.
  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(CREATE_TOOLTIP_KEY);
      if (!dismissed) {
        const t = setTimeout(() => setShowCreateTooltip(true), 900);
        return () => clearTimeout(t);
      }
    } catch (storageErr) {
      // localStorage unavailable — silently skip
      console.debug('localStorage read failed:', storageErr?.message);
    }
  }, []);

  const dismissCreateTooltip = () => {
    setShowCreateTooltip(false);
    try { localStorage.setItem(CREATE_TOOLTIP_KEY, '1'); } catch (storageErr) {
      // ignore storage errors
      console.debug('localStorage write failed:', storageErr?.message);
    }
  };

  // Handle showing subscription plans
  const handleShowSubscribe = () => {
    // Find and click the subscribe button in the header
    const subscribeBtn = document.querySelector('[data-testid="subscribe-btn"]');
    if (subscribeBtn) {
      subscribeBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => subscribeBtn.click(), 300);
    }
  };

  // Handle URL query parameters for direct market/category navigation
  useEffect(() => {
    const marketId = searchParams.get('market');
    const categoryId = searchParams.get('category');
    
    if (marketId) {
      // Fetch market and open modal
      apiService.getMarket(marketId).then(market => {
        if (market) {
          setSelectedMarket(market);
          setTradeOpen(true);
        }
      }).catch(err => console.error('Failed to load market:', err));
      // Clear the query param
      setSearchParams({});
    }
    
    if (categoryId) {
      setSelectedCategory(categoryId);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleMarketClick = (market) => {
    setSelectedMarket(market);
    setTradeOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO 
        title={HOMEPAGE_META.title}
        description={HOMEPAGE_META.description}
        keywords={HOMEPAGE_META.keywords}
        url={BASE_URL}
        jsonLd={HOMEPAGE_META.jsonLd}
      />
      <Header />
      {/* CREATE YOUR OWN MARKET — homepage banner (sits right under the Mock Predictions badge) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <div className="relative">
          <Link
            to="/ideas?create=1"
            onClick={() => {
              dismissCreateTooltip();
              trackFunnelEvent('banner_click', { source: 'homepage_banner' });
            }}
            data-testid="create-your-own-market-banner"
            className="group relative flex items-center justify-between gap-4 overflow-hidden rounded-2xl border border-[#F97316]/40 bg-gradient-to-r from-[#F97316]/15 via-[#3B82F6]/10 to-[#131b2e] px-5 py-4 shadow-[0_6px_0_0_#1E3A8A,0_10px_30px_rgba(37,99,235,0.25)] hover:shadow-[0_3px_0_0_#1E3A8A,0_6px_20px_rgba(37,99,235,0.3)] hover:translate-y-[3px] active:translate-y-[5px] active:shadow-[0_0_0_0_#1E3A8A] transition-all duration-150"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-[#F97316] to-[#EA580C] flex items-center justify-center shadow-lg">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-widest text-[#F97316] font-semibold">Launch an idea</p>
                <p className="text-base sm:text-lg font-extrabold text-white uppercase tracking-wide truncate">
                  Create Your Own Market
                </p>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] px-4 py-2 rounded-full shadow-[0_3px_0_0_#1E40AF] group-hover:shadow-[0_1px_0_0_#1E40AF] transition-all">
              Start now
              <ArrowRight className="h-4 w-4" />
            </div>
            <ArrowRight className="sm:hidden h-5 w-5 text-white flex-shrink-0" />
          </Link>

          {/* First-time onboarding tooltip */}
          {showCreateTooltip && (
            <div
              data-testid="create-market-tooltip"
              className="absolute left-1/2 sm:left-auto sm:right-6 -translate-x-1/2 sm:translate-x-0 top-full mt-3 z-30 w-[min(90vw,320px)] rounded-xl border border-[#F97316]/50 bg-[#0a1020] shadow-[0_20px_50px_rgba(0,0,0,0.55)] animate-fade-in"
            >
              <div
                className="absolute -top-2 right-10 sm:right-24 w-4 h-4 rotate-45 bg-[#0a1020] border-t border-l border-[#F97316]/50"
                aria-hidden="true"
              />
              <button
                type="button"
                onClick={dismissCreateTooltip}
                data-testid="dismiss-create-market-tooltip"
                aria-label="Dismiss"
                className="absolute top-2 right-2 text-gray-400 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
              <div className="p-4 pr-8">
                <p className="text-[10px] uppercase tracking-widest text-[#F97316] font-bold mb-1">New</p>
                <p className="text-sm font-semibold text-white leading-snug">
                  Propose a market the community can trade on.
                </p>
                <p className="text-xs text-gray-400 mt-1 leading-snug">
                  Tap here to submit a prediction question — the most-voted ideas go live.
                </p>
                <button
                  type="button"
                  onClick={dismissCreateTooltip}
                  className="mt-3 text-xs font-semibold text-[#3B82F6] hover:text-[#60A5FA] transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <CategoryBar
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
        closingDateFilter={closingDateFilter}
        onClosingDateChange={setClosingDateFilter}
      />

      {/* AEO answer block — H1 + one-sentence definition + short intro.
          Kept above-the-fold-adjacent so both users and AI answer engines
          get the canonical description on the homepage. */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6" data-testid="homepage-aeo">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-3">
          {HOMEPAGE_META.h1}
        </h1>
        <p className="text-base sm:text-lg text-gray-300 max-w-3xl leading-relaxed">
          {HOMEPAGE_META.aeoAnswer}
        </p>
      </section>

      <main>
        <FeaturedMarket />
        <ValueProps />
        
        {/* Markets Section Title */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
          <h2 className="text-xl font-bold text-white mb-4">Markets</h2>
        </div>
        
        <MarketGrid 
          selectedCategory={selectedCategory} 
          closingDateFilter={closingDateFilter}
          onMarketClick={handleMarketClick}
        />

        {/* SEO/AEO content (sections, internal links, FAQ) is preserved for
            crawlers via JSON-LD in <head> and the pre-rendered <noscript>
            block written by scripts/generate-static-pages.js at build time.
            We intentionally do not render it visibly here — the footer nav
            already exposes every internal link, and duplicating it on the
            homepage clutters the market grid. */}

        {/* Compact mobile-download callout, placed directly above the footer
            so returning users always see a clear path to iOS/Android. */}
        <section
          data-testid="homepage-mobile-download"
          className="max-w-5xl mx-auto px-4 sm:px-6 py-12"
        >
          <div className="rounded-2xl border border-[#1e2a45] bg-gradient-to-r from-[#0b1220] to-[#131b2e] px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="text-center sm:text-left">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                Get AMP Predict on your phone
              </h2>
              <p className="text-sm text-gray-400 max-w-md">
                Same account, same markets, same virtual TK¢ balance across iOS and Android.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <a
                href="https://apps.apple.com/us/app/amppredict/id6764166197"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-download-ios"
                className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm font-semibold hover:bg-gray-200 transition-colors"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                  <path d="M16.365 1.43c0 1.14-.42 2.08-1.26 2.82-.83.73-1.83 1.15-2.98 1.05-.14-1.05.39-2.13 1.14-2.85.83-.79 2-1.34 3.1-1.02zm4.36 16.34c-.53 1.23-.79 1.78-1.47 2.87-.95 1.52-2.29 3.41-3.95 3.43-1.48.02-1.86-.96-3.87-.95-2.01.01-2.43.97-3.9.95-1.67-.02-2.94-1.73-3.89-3.25-2.65-4.24-2.93-9.22-1.29-11.87 1.16-1.87 2.98-2.97 4.7-2.97 1.75 0 2.85.96 4.3.96 1.41 0 2.27-.96 4.29-.96 1.53 0 3.15.83 4.32 2.27-3.79 2.08-3.17 7.5.76 9.52z" />
                </svg>
                App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.anjolc.ampredict"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-download-android"
                className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm font-semibold hover:bg-gray-200 transition-colors"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                  <path d="M3.6 1.2c-.35.35-.6.9-.6 1.6v18.4c0 .7.25 1.25.6 1.6l10.55-10.8L3.6 1.2zm11.55 11.65 3.05 3.1-11.9 6.75c-.4.25-.85.35-1.3.3l10.15-10.15zm5.5-3.1-2.55-1.45-3.15 3.2 3.15 3.2 2.6-1.5c1.35-.75 1.35-2.7-.05-3.45zM5 1.4c.45-.05.9.05 1.3.3l11.9 6.75-3.05 3.05L5 1.4z" />
                </svg>
                Google Play
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      
      {/* Per-market structured data (Event + FinancialProduct + BreadcrumbList)
          + title/description override — rendered when a market is opened so
          deep links and AI crawlers see the specific market context. */}
      {tradeOpen && selectedMarket && <MarketJsonLd market={selectedMarket} />}

      {/* Orderbook Trading Modal - Kalshi Style */}
      <OrderbookModal 
        isOpen={tradeOpen} 
        onClose={() => setTradeOpen(false)} 
        market={selectedMarket}
        isSubscribed={isSubscribed}
        onShowSubscribe={handleShowSubscribe}
      />
    </div>
  );
};

export default HomePage;
